import { gql } from '@apollo/client';
import {
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { Ref, useMemo, useRef, useState } from 'react';
import { BulkUpdateFirmware } from '~components/displays/BulkAction/BulkUpdateFirmware';
import { ChevronRightIcon, SearchIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { useFeatureFlag } from '~utils/features';
import { isDefined } from '~utils/types';
import {
  BulkActionModal_CustomerFragment,
  BulkActionModal_DisplayFragment,
} from './__generated__/BulkActionModal.graphql';
import { BulkAddGroups } from './BulkAddGroups';
import { BulkEditSite } from './BulkEditSite';
import { BulkEditWebPages } from './BulkEditWebPages';
import { BulkManagePlaylist } from './BulkManagePlaylist';
import { BulkManagePowerSchedule } from './BulkManagePowerSchedule';
import { BulkRemoveGroups } from './BulkRemoveGroups';
import { BulkSyncDisplays } from './BulkSyncDisplays';
import { BulkUpdateContentSource } from './BulkUpdateContentSource';
import { BulkUpdatePower } from './BulkUpdatePower';
import { BulkUpdateVolume } from './BulkUpdateVolume';

interface Props {
  customer: BulkActionModal_CustomerFragment;
  displays: BulkActionModal_DisplayFragment[];
  initialFocusRef?: Ref<HTMLButtonElement>;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: () => Promise<void> | void;
  onBack?: () => Promise<void> | void;
}

export type BulkActionComponent = ((props: Props) => JSX.Element) & { graphql?: unknown };

type Action = {
  id: string;
  name: string;
  component: BulkActionComponent;
};

export function BulkActionModal(props: Props) {
  const { isOpen, onCancel } = props;
  const initialFocusRef = useRef(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel} closeOnEsc>
      <ModalOverlay />
      <BulkActionModalContent initialFocusRef={initialFocusRef} {...props} />
    </Modal>
  );
}

function BulkActionModalContent(props: Props) {
  const { isEnabled: isFirmwareEnabled } = useFeatureFlag('bulkFirmwareUpdate');
  const { isEnabled: isPlaylistEnabled } = useFeatureFlag('playlist');
  const { isEnabled: isPowerScheduleEnabled } = useFeatureFlag('powerSchedule');

  const actions: Action[] = useMemo(
    () =>
      [
        {
          id: 'power',
          name: 'On/Standby',
          component: BulkUpdatePower,
        },
        {
          id: 'edit-site',
          name: 'Edit Site',
          component: BulkEditSite,
        },
        {
          id: 'add-groups',
          name: 'Add Groups',
          component: BulkAddGroups,
        },
        {
          id: 'remove-groups',
          name: 'Remove Groups',
          component: BulkRemoveGroups,
        },
        {
          id: 'edit-web-pages',
          name: 'Edit Web Pages',
          component: BulkEditWebPages,
        },
        {
          id: 'update-content-source',
          name: 'Change Input Source',
          component: BulkUpdateContentSource,
        },
        {
          id: 'update-volume',
          name: 'Change Audio Settings',
          component: BulkUpdateVolume,
        },
        !isPlaylistEnabled
          ? undefined
          : {
              id: 'manage-playlist',
              name: 'Manage Playlist',
              component: BulkManagePlaylist,
            },
        {
          id: 'sync-playlists',
          name: 'Sync Playlists',
          component: BulkSyncDisplays,
        },
        !isPowerScheduleEnabled
          ? undefined
          : {
              id: 'manage-power-schedule',
              name: 'Manage Power Schedule',
              component: BulkManagePowerSchedule,
            },
        !isFirmwareEnabled
          ? undefined
          : {
              id: 'update-firmware',
              name: 'Update Firmware',
              component: BulkUpdateFirmware,
            },
      ].filter(isDefined),
    [isFirmwareEnabled, isPlaylistEnabled, isPowerScheduleEnabled],
  );

  const { onCancel } = props;
  const [searchValue, setSearchValue] = useState<string>('');
  const [action, setAction] = useState<Action | null>(null);

  const searchedActions = useMemo(() => {
    return searchValue === ''
      ? actions
      : actions.filter((filter) => filter.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [actions, searchValue]);

  return action === null ? (
    <ModalContent>
      <ModalHeader>Bulk action</ModalHeader>
      <ModalCloseButton onClick={onCancel} />

      <ModalBody>
        <InputGroup mb="4" width="100%">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.500" />
          </InputLeftElement>

          <Input
            onChange={(e) => setSearchValue(e.target.value)}
            bgColor="gray.50"
            borderColor="gray.100"
            placeholder="Search Actions"
          />
        </InputGroup>

        <VStack spacing="1" minHeight="36">
          {searchedActions.map((action, index) => {
            return (
              <Button
                key={action.id}
                ref={index === 0 ? props.initialFocusRef : undefined}
                rightIcon={<ChevronRightIcon />}
                variant="ghost"
                width="100%"
                justifyContent="space-between"
                paddingLeft="1"
                paddingRight="1"
                paddingY="1"
                border="none"
                color="blue.800"
                fontWeight="regular"
                _hover={{
                  fontWeight: 'semibold',
                }}
                _active={{
                  background: 'inherit',
                }}
                onClick={() => {
                  setAction(action);
                }}
              >
                {action.name}
              </Button>
            );
          })}
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </ModalContent>
  ) : (
    <action.component onBack={() => setAction(null)} {...props} />
  );
}

BulkActionModal.graphql = {
  fragments: {
    BulkActionModal_display: gql`
      fragment BulkActionModal_display on Display {
        id
        ...BulkRemoveGroups_display
        ...BulkUpdatePower_display
        ...BulkUpdateVolume_display
        ...BulkSyncDisplays_display
        ...BulkEditWebPages_display
        ...BulkUpdateContentSource_display
        ...BulkUpdateFirmware_display
      }
    `,
    BulkActionModal_customer: gql`
      fragment BulkActionModal_customer on Customer {
        id
        ...BulkEditSite_customer
        ...BulkAddGroups_customer
        ...BulkManagePlaylist_customer
        ...BulkManagePowerSchedule_customer
      }
    `,
  },
};
